/**
 *
 * @module html5PlayerAdapter
 * @desc Standard HTML5 PlayerAdapter
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class Html5PlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 * @example const playerAdapter = new Html5PlayerAdapter(videoElement);
 *
 */
export default class Html5PlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @throws {InvalidArgumentException}
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'Html5PlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.play), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line

        super(nativePlayer);

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Unable to set playlistUri on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'Html5PlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            },
            get setPlaylistUri() {
                return `${this.method} unable to set playlistUri on NativePlayer`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve, reject) => {

            this.playlistUri = playlistUri;

            try {

                const sourceElement = nativePlayer.getElementsByTagName('source')[0];

                /**
                 *
                 * @desc Certain HTML5 platforms like Tivo, Sony, Panasonic are not guaranteed to
                 * contain a child source element. If a source element does not exist, simply
                 * set the source directly on the media element.
                 *
                 */
                if (Check.assigned(sourceElement)) {
                    sourceElement.setAttribute('src', playlistUri);
                } else {
                    nativePlayer.setAttribute('src', playlistUri);
                }

                return resolve();

            } catch (ex) {

                const reasons = [new ErrorReason('', `${errorMessage.setPlaylistUri} - ${ex}`)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note nativePlayer.currentTime - The HTMLMediaElement.currentTime property gives
     * the current playback time in seconds
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'Html5PlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.assigned(nativePlayer.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'Html5PlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(nativePlayer.play)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                nativePlayer.onplay = boundHandlers.onPlay;
                nativePlayer.onpause = boundHandlers.onPause;
                nativePlayer.onerror = boundHandlers.onError;
                nativePlayer.onended = boundHandlers.onPlayedToCompletion;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'Html5PlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve) => {

            if (Check.function(nativePlayer.play) && Check.equal(this.listener, listener)) {

                this.listener = null;

                nativePlayer.onplay = null;
                nativePlayer.onpause = null;
                nativePlayer.onerror = null;
                nativePlayer.onended = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'Html5PlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;

        return Promise.resolve();

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/html5PlayerAdapter.js